import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  Facebook, Instagram, Linkedin, Twitter, 
  Save, Loader2, ExternalLink, Share2
} from 'lucide-react';
import { useCMS } from '../../context/CMSContext';

const AdminSocialLinksPage = () => {
  const { getSocialLinks, updateSocialLinks } = useCMS();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState({
    facebook: '',
    instagram: '',
    linkedin: '',
    twitter: ''
  });

  useEffect(() => {
    loadSocialLinks();
  }, []);

  const loadSocialLinks = async () => {
    try {
      const data = await getSocialLinks();
      setLinks({
        facebook: data.facebook || '',
        instagram: data.instagram || '',
        linkedin: data.linkedin || '',
        twitter: data.twitter || ''
      });
    } catch (error) {
      console.log('Using default social links');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSocialLinks(links);
      toast.success('Social media links updated successfully!');
    } catch (error) {
      toast.error('Failed to update social media links');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (platform, value) => {
    setLinks(prev => ({ ...prev, [platform]: value }));
  };

  const socialPlatforms = [
    {
      id: 'facebook',
      name: 'Facebook',
      icon: Facebook,
      placeholder: 'https://facebook.com/yourpage',
      color: 'bg-[#1877F2]',
      hoverColor: 'hover:bg-[#1877F2]/90'
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      placeholder: 'https://instagram.com/yourprofile',
      color: 'bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737]',
      hoverColor: 'hover:opacity-90'
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: Linkedin,
      placeholder: 'https://linkedin.com/company/yourcompany',
      color: 'bg-[#0A66C2]',
      hoverColor: 'hover:bg-[#0A66C2]/90'
    },
    {
      id: 'twitter',
      name: 'X (Twitter)',
      icon: Twitter,
      placeholder: 'https://x.com/yourhandle',
      color: 'bg-black',
      hoverColor: 'hover:bg-black/90'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-tech-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-social-links-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-navy flex items-center gap-2">
            <Share2 className="text-tech-blue" />
            Social Media Links
          </h1>
          <p className="text-text-secondary mt-1">
            Manage your social media profile links displayed in the website footer
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-tech-blue hover:bg-tech-blue/90"
          data-testid="save-social-links-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Social Links Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {socialPlatforms.map((platform) => {
          const Icon = platform.icon;
          const value = links[platform.id];
          
          return (
            <Card key={platform.id} className="overflow-hidden">
              <CardHeader className={`${platform.color} text-white py-4`}>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Icon className="w-5 h-5" />
                  {platform.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      Profile URL
                    </label>
                    <Input
                      type="url"
                      value={value}
                      onChange={(e) => handleChange(platform.id, e.target.value)}
                      placeholder={platform.placeholder}
                      className="w-full"
                      data-testid={`${platform.id}-input`}
                    />
                  </div>
                  
                  {value && (
                    <a
                      href={value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-tech-blue hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Preview Link
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle>Footer Preview</CardTitle>
          <CardDescription>
            This is how your social media links will appear in the website footer
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-primary-navy p-6 rounded-lg">
            <p className="text-white/70 mb-4 text-sm">Social Media Icons:</p>
            <div className="flex space-x-4">
              {socialPlatforms.map((platform) => {
                const Icon = platform.icon;
                const value = links[platform.id];
                const hasLink = value && value.trim() !== '';
                
                return (
                  <a
                    key={platform.id}
                    href={hasLink ? value : '#'}
                    target={hasLink ? '_blank' : undefined}
                    rel={hasLink ? 'noopener noreferrer' : undefined}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      hasLink 
                        ? 'bg-white/20 hover:bg-care-teal cursor-pointer' 
                        : 'bg-white/10 cursor-not-allowed opacity-50'
                    }`}
                    title={hasLink ? platform.name : `${platform.name} (no link set)`}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </a>
                );
              })}
            </div>
            {!Object.values(links).some(v => v && v.trim() !== '') && (
              <p className="text-white/50 text-xs mt-3">
                Add social media links above to see them in the preview
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tips Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-blue-800 mb-2">Tips for Social Media Links</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Use the full URL including https:// for each platform</li>
            <li>• Make sure your social profiles are public and professional</li>
            <li>• Keep your social media content aligned with your brand messaging</li>
            <li>• Links will appear in the website footer across all pages</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSocialLinksPage;
